/**
 * A true render of a moment on the ad's page (docs/SPEC.md §13.2, §15,
 * §25.5; P24-T05): Phase 13's panel (`live/TrueRenderPanel.tsx`, which stays
 * on the old job page until P25) moved onto the ad's routes —
 * `POST /v1/ads/{id}/stills {moment}`, `GET …/stills` (with the wait) and
 * `POST …/stills/{sid}/cancel` — and the price from `GET …/outputs/estimate`
 * (`still`), shown before anything is held.
 *
 * The button asks for the paused moment (also **See one moment first** on
 * the ray-traced final's card); the confirmation says the moment, the price
 * and the wait — it is the GPU render's confirm with its estimate (§15.1),
 * and a balance below it is the block with **Top up** (P28-T08); the render
 * in flight shows its step and **Stop**; the
 * pictures made so far, newest first, each with **Go to this moment** and
 * its download.
 */
import { useRef, useState } from "react";
import { newIdempotencyKey, shortfallOf } from "../api/client";
import { TopUp } from "../ui/Credits";
import {
  type Output,
  type StillWait,
  running,
  useAdStills,
  useCancelAdStill,
  useMakeStill,
  useOutputsEstimate,
} from "../api/outputs";
import { TRUE_RENDER, outputError, say } from "../copy/outputs";
import { Button, Card, Dialog, ImageLightbox } from "../ui";

/** The moment asked about, with what the dialog says of it. */
export interface MomentAsked {
  scene: string;
  t: number;
  /** The scene's number in the film and its name. */
  n: number;
  name: string;
  /** The person had turned the view: the render is the film's camera. */
  free: boolean;
}

const seconds = (t: number) => (Math.round(t * 10) / 10).toFixed(1);

/** What can be said of the wait for a render machine (Phase 13's rule). */
export function waitLine(wait: StillWait | null | undefined): string | null {
  if (!wait) return null;
  if (wait.machine === "unavailable") return TRUE_RENDER.waitUnavailable;
  if (wait.machine === "busy") return TRUE_RENDER.waitBusy;
  const minutes =
    wait.seconds === null ? null : Math.max(2, Math.round(wait.seconds / 60));
  if (wait.machine === "cold") {
    return minutes === null
      ? TRUE_RENDER.waitCold
      : say(TRUE_RENDER.waitColdMinutes, { minutes });
  }
  if (wait.seconds === null || minutes === null) return TRUE_RENDER.waitWarm;
  return wait.seconds < 90
    ? say(TRUE_RENDER.waitWarmSeconds, {
        seconds: Math.max(5, Math.round(wait.seconds / 5) * 5),
      })
    : say(TRUE_RENDER.waitWarmMinutes, { minutes });
}

/** The sentence of a true render that ended without a picture, or null. */
export function endedLine(still: Pick<Output, "state">): string | null {
  if (still.state === "cancelled") return TRUE_RENDER.cancelled;
  if (still.state === "failed") return TRUE_RENDER.failed;
  return null;
}

function Confirmation({
  adId,
  asking,
  wait,
  onClose,
  onDone,
}: {
  adId: string;
  asking: MomentAsked;
  wait: StillWait | null;
  onClose(): void;
  onDone(reused: boolean): void;
}) {
  const estimate = useOutputsEstimate(adId, true);
  const make = useMakeStill(adId);
  const key = useRef<string | null>(null);
  const sending = useRef(false);
  const price = estimate.data?.still;
  const onConfirm = () => {
    if (sending.current) return;
    sending.current = true;
    key.current ??= newIdempotencyKey();
    make.mutate(
      {
        moment: { scene: asking.scene, t: Math.round(asking.t * 100) / 100 },
        idempotencyKey: key.current,
      },
      {
        onSuccess: ({ reused }) => onDone(reused),
        onSettled: () => {
          key.current = null;
          sending.current = false;
        },
      },
    );
  };
  const waiting = waitLine(wait);
  const short = shortfallOf(make.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title={TRUE_RENDER.title}
      footer={
        <>
          {price ? (
            <Button
              type="button"
              variant="primary"
              disabled={make.isPending}
              aria-busy={make.isPending}
              onClick={onConfirm}
            >
              {say(TRUE_RENDER.confirm, { credits: price.credits })}
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose}>
            {TRUE_RENDER.cancel}
          </Button>
        </>
      }
    >
      <div
        className="flex flex-col gap-2 text-sm text-slate-700"
        data-testid="still-dialog"
        aria-busy={estimate.isPending}
      >
        <p className="m-0">
          {say(TRUE_RENDER.hint, {
            n: asking.n,
            name: asking.name,
            time: seconds(asking.t),
          })}
        </p>
        {asking.free ? <p className="m-0">{TRUE_RENDER.filmCamera}</p> : null}
        {price ? (
          <p className="m-0" data-testid="still-cost">
            {say(TRUE_RENDER.cost, {
              credits: price.credits,
              seconds: Math.round(price.seconds),
            })}
          </p>
        ) : null}
        {waiting ? <p className="m-0">{waiting}</p> : null}
        {estimate.isError ? (
          <p className="m-0" role="alert">
            {outputError(estimate.error)}
          </p>
        ) : null}
        {short ? (
          <TopUp shortfall={short} />
        ) : make.isError ? (
          <p className="m-0" role="alert">
            {outputError(make.error)}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function Picture({
  still,
  label,
  onGoTo,
}: {
  still: Output;
  label: string;
  onGoTo(): void;
}) {
  const [open, setOpen] = useState(false);
  const alt = label;
  return (
    <li className="flex flex-col gap-1" data-testid="still-picture">
      <button
        type="button"
        className="self-start rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
        onClick={() => setOpen(true)}
      >
        <img
          src={still.url ?? undefined}
          alt={alt}
          className="block h-24 w-auto rounded-md bg-slate-100"
          loading="lazy"
        />
      </button>
      <span className="text-xs text-slate-700">{label}</span>
      <span className="flex flex-wrap gap-1">
        <Button type="button" variant="ghost" size="sm" onClick={onGoTo}>
          {TRUE_RENDER.goto}
        </Button>
        {still.url ? (
          <Button as="a" href={still.url} variant="ghost" size="sm">
            {TRUE_RENDER.download}
          </Button>
        ) : null}
      </span>
      {open && still.url ? (
        <ImageLightbox
          src={still.url}
          alt={alt}
          label={alt}
          fit="contain"
          onClose={() => setOpen(false)}
        />
      ) : null}
    </li>
  );
}

export function TrueRenderPanel({
  adId,
  playing,
  asking,
  onAsk,
  onAskClose,
  sceneOf,
  onGoTo,
}: {
  adId: string;
  playing: boolean;
  /** The moment the confirmation is open on, or null. */
  asking: MomentAsked | null;
  /** The button: ask about the paused moment. */
  onAsk(): void;
  onAskClose(): void;
  /** A scene's number and name in the film, for a picture's label. */
  sceneOf(sceneId: string): { n: number; name: string } | null;
  /** Go to a picture's moment in the player. */
  onGoTo(moment: { scene: string; t: number }): void;
}) {
  const stills = useAdStills(adId);
  const cancel = useCancelAdStill(adId);
  const [reused, setReused] = useState(false);
  const stopKey = useRef<string | null>(null);
  const items = stills.data?.items ?? [];
  const flight = items.find(running) ?? null;
  const latest = items[0] ?? null;
  const ended = latest && !flight ? endedLine(latest) : null;
  const pictures = items.filter((s) => s.state === "done" && s.url);

  const labelOf = (s: Output) => {
    const scene = s.moment ? sceneOf(s.moment.scene) : null;
    return scene && s.moment
      ? say(TRUE_RENDER.label, { n: scene.n, time: seconds(s.moment.t) })
      : TRUE_RENDER.title;
  };

  return (
    <Card
      className="flex flex-col gap-3 p-4"
      role="region"
      aria-label={TRUE_RENDER.title}
      data-testid="true-render"
    >
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-sm font-semibold text-slate-800">
          {TRUE_RENDER.title}
        </h2>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={playing || !!flight}
          title={playing ? TRUE_RENDER.pauseFirst : undefined}
          onClick={() => {
            setReused(false);
            onAsk();
          }}
        >
          {TRUE_RENDER.button}
        </Button>
        {reused ? (
          <span role="status" className="text-sm text-slate-700">
            {TRUE_RENDER.reused}
          </span>
        ) : null}
      </div>
      {flight ? (
        <div
          className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3"
          data-testid="still-flight"
        >
          <span className="text-sm font-medium text-slate-800">
            {labelOf(flight)}
          </span>
          <span role="status" className="text-sm text-slate-700">
            {flight.state === "working"
              ? TRUE_RENDER.rendering
              : TRUE_RENDER.pending}
          </span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={cancel.isPending}
            aria-busy={cancel.isPending}
            onClick={() => {
              stopKey.current ??= newIdempotencyKey();
              cancel.mutate(
                { id: flight.id, idempotencyKey: stopKey.current },
                { onSettled: () => (stopKey.current = null) },
              );
            }}
          >
            {TRUE_RENDER.stop}
          </Button>
          {flight.state === "queued" && waitLine(stills.data?.wait) ? (
            <p className="m-0 w-full text-xs text-slate-600">
              {waitLine(stills.data?.wait)}
            </p>
          ) : null}
        </div>
      ) : null}
      {ended !== null && latest ? (
        <p className="m-0 text-sm text-slate-700" role="status">
          {labelOf(latest)}: {ended}
        </p>
      ) : null}
      {cancel.isError ? (
        <p className="m-0 text-sm text-slate-700" role="alert">
          {outputError(cancel.error)}
        </p>
      ) : null}
      {pictures.length > 0 ? (
        <ul
          className="m-0 flex list-none flex-wrap gap-3 p-0"
          data-testid="still-pictures"
        >
          {pictures.map((s) => (
            <Picture
              key={s.id}
              still={s}
              label={labelOf(s)}
              onGoTo={() => s.moment && onGoTo(s.moment)}
            />
          ))}
        </ul>
      ) : null}
      {asking ? (
        <Confirmation
          adId={adId}
          asking={asking}
          wait={stills.data?.wait ?? null}
          onClose={onAskClose}
          onDone={(wasReused) => {
            onAskClose();
            setReused(wasReused);
          }}
        />
      ) : null}
    </Card>
  );
}
